import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../store';
import type {
  PassListFilterValues,
  PassListSortValues,
  PassListStatus,
  NormalisedPassListItem,
} from '../../types/pass';

const PAGE_SIZE = 10;

export const initialSortValues: PassListSortValues = {
  sortBy: '',
  sortOrder: '',
};

export const initialFilterValues: PassListFilterValues = {
  id: '',
  extId: '',
  extIdPrefix: '',
  passTemplate: [],
  groupTag: [],
  passEntity: '',
  status: [],
  section: '',
  row: '',
  passType: [],
  deviceType: '',
  search: '',
  addedToWalletDate: '',
};

export interface PassListPagination {
  items: NormalisedPassListItem[];
  total: number;
  pageIndex: number;
  pageSize: number;
  pageCount: number;
  status: PassListStatus;
  loading: boolean;
  error: string | null;
}

export const initialPasses: PassListPagination = {
  items: [],
  total: 0,
  pageIndex: 0,
  pageSize: PAGE_SIZE,
  pageCount: 0,
  status: 'idle',
  loading: false,
  error: null,
};

interface PassListState {
  passes: PassListPagination;
  filterValues: PassListFilterValues;
  sortValues: PassListSortValues;
  totalActiveFilters: number;
  clearPageIndex: number;
  newFiltersSet: boolean;
  navigatedFromPassPage: boolean;
}

const initialState: PassListState = {
  passes: initialPasses,
  filterValues: initialFilterValues,
  sortValues: initialSortValues,
  totalActiveFilters: 0,
  clearPageIndex: 0,
  newFiltersSet: false,
  navigatedFromPassPage: false,
};

const slice = createSlice({
  name: 'passList',
  initialState,
  reducers: {
    updatePasses(state, action: PayloadAction<PassListPagination>) {
      state.passes = action.payload;
    },

    mergePasses(state, action: PayloadAction<Partial<PassListPagination>>) {
      Object.assign(state.passes, action.payload);
    },

    updateFilterValues(state, action: PayloadAction<PassListFilterValues>) {
      state.filterValues = action.payload;
      state.clearPageIndex += 1;
      state.newFiltersSet = true;
    },

    setNewFiltersSet(state, action: PayloadAction<boolean>) {
      state.newFiltersSet = action.payload;
    },

    updateSortValues(state, action: PayloadAction<PassListSortValues>) {
      state.sortValues = action.payload;
    },

    updateTotalActiveFilters(state, action: PayloadAction<number>) {
      state.totalActiveFilters = action.payload;
    },

    resetPageIndex(state) {
      state.passes.pageIndex = 0;
    },

    setNavigatedFromPassPage(state, action: PayloadAction<boolean>) {
      state.navigatedFromPassPage = action.payload;
    },

    resetAll(state) {
      state.passes = initialPasses;
      state.filterValues = initialFilterValues;
      state.sortValues = initialSortValues;
      state.totalActiveFilters = 0;
      state.clearPageIndex = 0;
      state.newFiltersSet = false;
      state.navigatedFromPassPage = false;
    },
  },
});

export const {
  updatePasses,
  mergePasses,
  updateFilterValues,
  updateSortValues,
  updateTotalActiveFilters,
  setNewFiltersSet,
  setNavigatedFromPassPage,
  resetPageIndex,
  resetAll,
} = slice.actions;

export default slice.reducer;

export const selectPasses = (state: RootState) => state.passList.passes;
export const selectFilterValues = (state: RootState) =>
  state.passList.filterValues;
export const selectTotalActiveFilters = (state: RootState) =>
  state.passList.totalActiveFilters;
export const selectClearPageIndex = (state: RootState) =>
  state.passList.clearPageIndex;
export const selectNewFiltersSet = (state: RootState) =>
  state.passList.newFiltersSet;
export const selectSortValues = (state: RootState) => state.passList.sortValues;
export const selectNavigatedFromPassPage = (state: RootState) =>
  state.passList.navigatedFromPassPage;
